import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Screen from '../../components/common/Screen';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Icon from '../../components/common/Icon';
import { apiRequest } from '../../utils';
import './MemberHome.css';

/* Public host screen — the photo, a PUBLIC bio, and clickable social links for a Bravos Lab host.
   When the host is also a listed specialist, the payload carries the specialist fields (headline,
   community roles, member discount, services) and they render here too — same blocks as the public
   specialist page, so one link covers the whole public profile.
   Reached from the host chip on the public event page. Backend: GET /api/host/:id (no auth). */
const HostProfile = () => {
  const { t } = useTranslation();
  // ★ The route is /host/:partnerId — destructuring `id` here yields undefined and the page
  //   fetches /api/host/undefined → 404 → "host not found" for every host.
  const { partnerId: id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiRequest('GET', `/host/${id}`)
      .then((res) => { if (!cancelled) setP(res); })
      .catch(() => { if (!cancelled) setMissing(true); });
    return () => { cancelled = true; };
  }, [id]);

  const socials = p ? [
    p.linkedin && { k: 'linkedin', name: 'linkedin', pack: 'brands', url: p.linkedin },
    p.instagram && { k: 'instagram', name: 'instagram', pack: 'brands', url: p.instagram },
    p.facebook && { k: 'facebook', name: 'facebook', pack: 'brands', url: p.facebook },
    p.twitter && { k: 'twitter', name: 'twitter', pack: 'brands', url: p.twitter },
    p.website && { k: 'website', name: 'globe', pack: 'br', url: p.website },
  ].filter(Boolean) : [];

  const fullName = p ? [p.first_name, p.name].filter(Boolean).join(' ') : '';

  return (
    <Screen>
      <div className="top">
        <Card>
          <button type="button" className="bravos-wallet__link bravos-members__back"
                  onClick={() => navigate(-1)}>
            <Icon name="angle-left" /> {t('back')}
          </button>
        </Card>

        {missing ? (
          <Card><p className="text-muted mb-0">{t('hostNotFound')}</p></Card>
        ) : p === null ? (
          <Card><span className="auth__spinner" /></Card>
        ) : (
          <Card className="bravos-profile-card">
            <div className="bravos-spec bravos-spec--full">
              {p.photo_url
                ? <img className="bravos-spec__photo" src={p.photo_url} alt="" />
                : <span className="bravos-spec__photo bravos-spec__photo--ph">
                    {(fullName || '?').slice(0, 1)}
                  </span>}
              <div className="bravos-spec__body">
                <Title my="0" align="left" className="mt-1">{fullName}</Title>
                {p.headline && <div className="bravos-spec__headline">{p.headline}</div>}
                {(p.roles || []).length > 0 && (
                  <div className="bravos-brand__roles">
                    {p.roles.map((r) => (
                      <span key={r.name} className="bravos-role-chip"
                            style={r.color ? { borderColor: r.color, color: r.color } : {}}>
                        {r.name}
                      </span>
                    ))}
                  </div>
                )}
                {socials.length > 0 && (
                  <div className="bravos-social">
                    {socials.map((s) => (
                      <a key={s.k} className="bravos-social__link" href={s.url}
                         target="_blank" rel="noreferrer" aria-label={s.k}>
                        <Icon name={s.name} pack={s.pack} />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {p.public_bio && (
              <p className="bravos-fb-block__text mt-3" style={{ whiteSpace: 'pre-line' }}>
                {p.public_bio}
              </p>
            )}

            {/* SPECIALIST BLOCK — only when this host is a listed (approved) specialist */}
            {p.discount_note && (
              <p className="bravos-spec__discount mt-2">
                <Icon name="ticket" className="me-1" />{p.discount_note}
              </p>
            )}

            {(p.services || []).length > 0 && (
              <>
                <p className="bravos-cal__label">{t('servicesOffered')}</p>
                <ul className="bravos-spec__services">
                  {p.services.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </>
            )}
          </Card>
        )}
      </div>
    </Screen>
  );
};

export default HostProfile;
